/* Sovereign IQ — Mobile PWA shell at /app
 * Minimal 3-tab flow: Home (pick country+sector → start blueprint),
 * Notifications (in-app alerts), Reports (view + download PDF).
 * Designed for phone viewports; heavy web features live at /platform.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Home as HomeIcon, Bell, FileText, Download, Eye, Loader2, Play,
  CheckCircle2, AlertTriangle, ChevronRight, LogOut, Search, Globe,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─────────────── Root shell with bottom-nav ───────────────
export default function MobileApp({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => { if (!user) navigate('/login?redirect=/app'); }, [user, navigate]);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setInstallPrompt(e); };
    const onInstalled = () => { setInstalled(true); setInstallPrompt(null); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    // Running standalone already?
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) setInstalled(true);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (!user) return null;
  const tab = location.pathname.replace('/app', '').split('/')[1] || 'home';

  const install = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') toast.success('Installed! Find Sovereign IQ on your home screen.');
    setInstallPrompt(null);
  };

  return (
    <div className="min-h-screen bg-[#0b1c30] text-white" data-testid="mobile-app-root">
      {/* Top bar */}
      <header className="sticky top-0 z-30 px-4 pt-[max(env(safe-area-inset-top),12px)] pb-3 bg-gradient-to-b from-[#0b1c30] to-[#0b1c30]/95 backdrop-blur border-b border-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#004ac6] to-[#7c3aed] flex items-center justify-center shadow-lg">
              <span className="text-[11px] font-black text-white tracking-tight">SiQ</span>
            </div>
            <div>
              <p className="text-[11px] text-white/50 leading-none">Welcome</p>
              <p className="text-[13px] font-bold leading-tight">{user.full_name || user.email}</p>
            </div>
          </div>
          <button onClick={onLogout} data-testid="mobile-logout-btn"
            className="p-2 text-white/50 hover:text-white active:scale-95 transition-all">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Pages */}
      <main className="pb-28 min-h-[calc(100vh-100px)]">
        {installPrompt && !installed && (
          <div className="mx-4 mt-4 p-3.5 rounded-xl bg-gradient-to-r from-[#7c3aed]/20 to-[#ec4899]/20 border border-[#7c3aed]/40 flex items-center gap-3" data-testid="install-prompt-banner">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
              <Download className="w-4 h-4 text-[#ec4899]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12.5px] font-bold leading-tight">Install Sovereign IQ</p>
              <p className="text-[10.5px] text-white/50 leading-snug mt-0.5">Add to your home screen for faster access & notifications.</p>
            </div>
            <button onClick={install} data-testid="install-btn"
              className="shrink-0 px-3 h-8 rounded-lg bg-gradient-to-r from-[#7c3aed] to-[#ec4899] text-[11px] font-black uppercase tracking-widest active:scale-95 transition-all">
              Install
            </button>
          </div>
        )}
        <Routes>
          <Route path="/" element={<HomeScreen user={user} />} />
          <Route path="/notifications" element={<NotificationsScreen />} />
          <Route path="/reports" element={<ReportsScreen user={user} />} />
          <Route path="*" element={<Navigate to="/app" replace />} />
        </Routes>
      </main>

      {/* Bottom tab bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-[#0b1c30] border-t border-white/10 backdrop-blur-xl pb-[max(env(safe-area-inset-bottom),8px)]">
        <div className="grid grid-cols-3 max-w-md mx-auto">
          <TabLink to="/app" icon={HomeIcon} label="Home" active={tab === 'home'} />
          <TabLink to="/app/notifications" icon={Bell} label="Alerts" active={tab === 'notifications'} withBadge />
          <TabLink to="/app/reports" icon={FileText} label="Reports" active={tab === 'reports'} />
        </div>
      </nav>
    </div>
  );
}

function TabLink({ to, icon: Icon, label, active, withBadge }) {
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!withBadge) return;
    let alive = true;
    const fetchUnread = () => axios.get(`${API}/mobile/notifications?limit=1`)
      .then(r => alive && setUnread(r.data.unread || 0)).catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [withBadge]);

  return (
    <Link to={to} data-testid={`mobile-tab-${label.toLowerCase()}`}
      className={`relative flex flex-col items-center gap-1 py-2.5 px-2 transition-all ${active ? 'text-[#7c3aed]' : 'text-white/50 hover:text-white/80'}`}>
      <div className="relative">
        <Icon className="w-5 h-5" strokeWidth={active ? 2.5 : 1.8} />
        {withBadge && unread > 0 && (
          <span data-testid="notif-badge"
            className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#ec4899] text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-[#0b1c30]">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </div>
      <span className={`text-[10px] font-bold uppercase tracking-widest ${active ? 'text-[#7c3aed]' : ''}`}>{label}</span>
      {active && <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[2px] bg-[#7c3aed] rounded-full" />}
    </Link>
  );
}

// ─────────────── HOME · pick country + sector + start ───────────────
function HomeScreen({ user }) {
  const [countries, setCountries] = useState([]);
  const [q, setQ] = useState('');
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [status, setStatus] = useState(null); // automation status
  const [starting, setStarting] = useState(false);

  const loadCountries = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/countries`);
      setCountries(r.data || []);
    } catch { toast.error('Failed to load countries'); }
  }, []);

  useEffect(() => { loadCountries(); }, [loadCountries]);

  // Poll automation status every 4s when running
  useEffect(() => {
    let alive = true;
    const poll = () => {
      axios.get(`${API}/admin/automation/status`).then(r => {
        if (alive) setStatus(r.data);
      }).catch(() => {});
    };
    poll();
    const t = setInterval(poll, 4000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return countries.slice(0, 40);
    return countries.filter(c =>
      (c.name || '').toLowerCase().includes(s) ||
      (c.sector || '').toLowerCase().includes(s)
    ).slice(0, 40);
  }, [countries, q]);

  const selectedCountry = countries.find(c => c.id === selectedCountryId);

  const start = async () => {
    if (!selectedCountryId) { toast.error('Pick a country first'); return; }
    if (status?.running) { toast.error('Another blueprint is already generating'); return; }
    setStarting(true);
    try {
      await axios.post(`${API}/mobile/automation/start`, { country_id: selectedCountryId });
      toast.success(`Blueprint started for ${selectedCountry?.name}`);
      // Ask for push permission on first start
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start');
    } finally { setStarting(false); }
  };

  const running = status?.running;
  const progress = status?.progress_pct || 0;

  return (
    <div className="px-4 pt-5 space-y-5" data-testid="mobile-home">
      {running && (
        <div className="p-4 rounded-xl bg-gradient-to-br from-[#7c3aed]/20 to-[#004ac6]/20 border border-[#7c3aed]/40" data-testid="mobile-progress-card">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-[#7c3aed]" />
              <p className="text-[13px] font-bold">Generating {status.country_name || 'blueprint'}</p>
            </div>
            <span className="text-[13px] font-black text-[#7c3aed]">{progress}%</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#7c3aed] to-[#ec4899] transition-all duration-700" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-[11px] text-white/60 mt-2 truncate">
            {status.current_label || 'Starting…'}
            {status.current_section ? ` · ${status.current_section.replace(/_/g, ' ')}` : ''}
          </p>
        </div>
      )}

      <section>
        <h2 className="text-[11px] font-black uppercase tracking-[0.2em] text-white/40 mb-3">Start a blueprint</h2>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input type="text" value={q} onChange={e => setQ(e.target.value)} data-testid="mobile-country-search"
            placeholder="Search country or sector…"
            className="w-full h-11 pl-10 pr-4 rounded-xl bg-white/5 border border-white/10 text-sm placeholder-white/30 focus:outline-none focus:border-[#7c3aed] focus:bg-white/10 transition-all" />
        </div>
        <div className="space-y-1.5 max-h-[340px] overflow-y-auto -mx-4 px-4" data-testid="mobile-country-list">
          {filtered.length === 0 ? (
            <p className="text-center text-white/40 text-xs py-6">No matches. Admin must add countries in the web app.</p>
          ) : filtered.map(c => {
            const picked = c.id === selectedCountryId;
            return (
              <button key={c.id} onClick={() => setSelectedCountryId(c.id)} data-testid={`mobile-country-${c.id}`}
                className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all active:scale-[0.98] ${picked ? 'bg-gradient-to-r from-[#7c3aed]/30 to-[#004ac6]/30 border border-[#7c3aed]' : 'bg-white/5 hover:bg-white/10 border border-white/5'}`}>
                {c.flag_url ? <img src={c.flag_url} alt="" className="w-8 h-6 rounded object-cover" /> : <Globe className="w-8 h-6 text-white/40" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{c.name}</p>
                  <p className="text-[11px] text-white/50 truncate">{c.sector || 'General'}</p>
                </div>
                {picked && <CheckCircle2 className="w-4 h-4 text-[#7c3aed] shrink-0" />}
              </button>
            );
          })}
        </div>
      </section>

      <button onClick={start} disabled={!selectedCountryId || running || starting} data-testid="mobile-start-btn"
        className="w-full h-14 rounded-2xl font-black text-sm uppercase tracking-widest transition-all active:scale-[0.97] flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed bg-gradient-to-r from-[#7c3aed] to-[#ec4899] text-white shadow-[0_8px_32px_rgba(124,58,237,0.4)]">
        {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 fill-white" />}
        {running ? 'Blueprint generating…' : starting ? 'Starting…' : 'Start Blueprint'}
      </button>

      <p className="text-center text-[10px] text-white/40 px-4 leading-relaxed">
        A full 31-section blueprint takes ~90 minutes. You'll be notified when it's ready — you can close this app.
      </p>
    </div>
  );
}

// ─────────────── NOTIFICATIONS ───────────────
function NotificationsScreen() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(() => {
    axios.get(`${API}/mobile/notifications`).then(r => {
      setItems(r.data.notifications || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  const tap = async (n) => {
    try { await axios.post(`${API}/mobile/notifications/${n.id}/read`); } catch {}
    if (n.link) navigate(n.link);
    else load();
  };

  const markAll = async () => {
    await axios.post(`${API}/mobile/notifications/read-all`).catch(() => {});
    load();
  };

  return (
    <div className="px-4 pt-5 space-y-3" data-testid="mobile-notifications">
      <div className="flex items-center justify-between">
        <h2 className="text-[11px] font-black uppercase tracking-[0.2em] text-white/40">Notifications</h2>
        {items.some(i => !i.read) && (
          <button onClick={markAll} data-testid="mobile-markall-btn"
            className="text-[10px] font-bold uppercase tracking-widest text-[#7c3aed] hover:text-[#ec4899]">Mark all read</button>
        )}
      </div>
      {loading ? <Loader2 className="w-5 h-5 animate-spin text-white/40 mx-auto mt-10" /> :
        items.length === 0 ? (
          <div className="text-center py-20">
            <Bell className="w-10 h-10 text-white/15 mx-auto mb-3" />
            <p className="text-sm text-white/40">No notifications yet</p>
            <p className="text-[11px] text-white/25 mt-1">You'll get alerted here when a blueprint is ready.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(n => <NotifRow key={n.id} n={n} onTap={() => tap(n)} />)}
          </div>
        )}
    </div>
  );
}

function NotifRow({ n, onTap }) {
  const palette = {
    blueprint_ready: { bg: 'from-[#10b981]/15 to-transparent', icon: CheckCircle2, tint: 'text-[#10b981]' },
    pdf_ready:       { bg: 'from-[#7c3aed]/15 to-transparent', icon: Download, tint: 'text-[#7c3aed]' },
    report_viewed:   { bg: 'from-[#004ac6]/15 to-transparent', icon: Eye, tint: 'text-[#60a5fa]' },
    info:            { bg: 'from-white/5 to-transparent', icon: Bell, tint: 'text-white/60' },
  }[n.type] || { bg: 'from-white/5 to-transparent', icon: Bell, tint: 'text-white/60' };
  const Icon = palette.icon;
  return (
    <button onClick={onTap} data-testid={`mobile-notif-${n.id}`}
      className={`w-full text-left p-3.5 rounded-xl flex items-start gap-3 transition-all active:scale-[0.98] bg-gradient-to-r ${palette.bg} border ${n.read ? 'border-white/5 opacity-70' : 'border-white/10'}`}>
      <div className={`w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0 ${palette.tint}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-bold leading-snug">{n.title}</p>
        <p className="text-[11px] text-white/50 leading-snug mt-0.5 line-clamp-2">{n.body}</p>
        <p className="text-[9px] text-white/30 mt-1.5 uppercase tracking-widest">
          {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
        </p>
      </div>
      {!n.read && <div className="w-2 h-2 rounded-full bg-[#ec4899] mt-1.5 shrink-0" />}
    </button>
  );
}

// ─────────────── REPORTS ───────────────
function ReportsScreen({ user }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(() => {
    axios.get(`${API}/mobile/reports`).then(r => {
      setReports(r.data.reports || []); setLoading(false);
    }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const download = async (r) => {
    try {
      toast.loading(`Preparing PDF…`, { id: `dl-${r.id}` });
      const res = await axios.get(`${API}/countries/${r.id}/blueprint/pdf`, { responseType: 'blob', timeout: 120000 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `${r.name}_Blueprint.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.dismiss(`dl-${r.id}`);
      toast.success('Downloaded');
    } catch (e) {
      toast.dismiss(`dl-${r.id}`);
      toast.error(e.response?.status === 404 ? 'PDF not yet ready' : 'Download failed');
    }
  };

  return (
    <div className="px-4 pt-5 space-y-3" data-testid="mobile-reports">
      <h2 className="text-[11px] font-black uppercase tracking-[0.2em] text-white/40">Your reports</h2>
      {loading ? <Loader2 className="w-5 h-5 animate-spin text-white/40 mx-auto mt-10" /> :
        reports.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="w-10 h-10 text-white/15 mx-auto mb-3" />
            <p className="text-sm text-white/40">No reports yet</p>
            <p className="text-[11px] text-white/25 mt-1 px-8">Start a blueprint from the Home tab — it'll appear here when done.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {reports.map(r => (
              <div key={r.id} className="p-3.5 rounded-xl bg-white/5 border border-white/10" data-testid={`mobile-report-${r.id}`}>
                <div className="flex items-center gap-3 mb-3">
                  {r.flag_url ? <img src={r.flag_url} alt="" className="w-10 h-7 rounded object-cover" /> : <Globe className="w-10 h-7 text-white/40" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate">{r.name}</p>
                    <p className="text-[11px] text-white/50 truncate">{r.sector || 'General'}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] text-white/40">Sections</p>
                    <p className="text-sm font-black">{r.sections_done}/{r.total_sections}</p>
                  </div>
                </div>
                <div className="h-1 bg-white/5 rounded-full overflow-hidden mb-3">
                  <div className="h-full bg-gradient-to-r from-[#7c3aed] to-[#ec4899]" style={{ width: `${(r.sections_done / r.total_sections) * 100}%` }} />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => navigate(`/platform/countries/${r.id}/blueprint`)} data-testid={`mobile-view-${r.id}`}
                    className="flex-1 h-10 rounded-lg bg-white/10 hover:bg-white/15 text-[12px] font-bold uppercase tracking-widest flex items-center justify-center gap-1.5 active:scale-[0.97] transition-all">
                    <Eye className="w-3.5 h-3.5" /> View
                  </button>
                  <button onClick={() => download(r)} disabled={!r.pdf_ready} data-testid={`mobile-download-${r.id}`}
                    className="flex-1 h-10 rounded-lg bg-gradient-to-r from-[#7c3aed] to-[#ec4899] disabled:opacity-40 disabled:cursor-not-allowed text-[12px] font-bold uppercase tracking-widest text-white flex items-center justify-center gap-1.5 active:scale-[0.97] transition-all">
                    <Download className="w-3.5 h-3.5" /> PDF
                  </button>
                </div>
                {!r.pdf_ready && <p className="text-[9px] text-white/30 text-center mt-2 uppercase tracking-widest">PDF not yet generated — run automation first</p>}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
